import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import {
  Button,
  IconButton,
  MenuItem,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Title from "../../../../../components/Title";
import { debounce } from "lodash";
import {
  fetchAllSystems,
  deleteSystem,
} from "../../../../../features/slices/SystemRegistry";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../store";
import { GlobalModal } from "../../../../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import { useParams, useNavigate } from "react-router-dom";
import Back from "../../../../../components/Back";
import { Registry } from "../../../../../interfaces";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import DialogConfirm from "../../../../../components/DialogConfirm";
import { useSnackbar } from "../../../../../context/SnackbarContext";

const SystemRegistry = () => {
  const [metaModal, setMetaModal] = useState(false);
  const { agentId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Registry[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "id", desc: true },
  ]);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };

  const handleEditSystem = (id: number, type: string) => {
    navigate(`/management/system-registry/edit/${id}/${type}`);
  };

  const handleOpenDeleteDialog = (id: number) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteSystem(deletingId)).unwrap();
      showSnackbar("Registry deleted successfully", "success");
      fetchData();
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to delete registry", "error");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      try {
        const data = await dispatch(fetchAllSystems()).unwrap();
        setData(Array.isArray(data) ? data : []);
      } catch (error) {
        setIsRefetching(false);
        setIsError(true);
        setIsLoading(false);
      } finally {
        setIsRefetching(false);
        setIsLoading(false);
      }
    }, 300),
    [
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      globalFilter,
      columnFilters,
      dispatch,
      agentId,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<Registry>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "title",
        header: "Title",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "user_id",
        header: "User ID",
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "repo",
        header: "Repo",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "reg_value",
        header: "Reg Value",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Button
            onClick={() => handleMetaOpen(cell.getValue<any>())}
            variant="outlined"
            size="small"
          >
            View
          </Button>
        ),
      },
      {
        accessorKey: "entity_type",
        header: "Type",
      },
      {
        accessorKey: "reg_type",
        header: "Reg Type",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableContainerProps: { sx: { maxHeight: "calc(78vh - 112px)" } },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, bgcolor: "background.default", color: "text.secondary", letterSpacing: 0.2 },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "comfortable",
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
      sorting: [{ id: "created_at", desc: false }],
    },
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnFilters,
      globalFilter,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/management/system-registry/create`)}
        >
          New entry
        </Button>
        <Tooltip arrow title="Refresh Data">
          <IconButton onClick={() => fetchData()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem
        key="edit"
        onClick={() =>
          handleEditSystem(Number(row.id), row.original.entity_type)
        }
        sx={{ m: 0 }}
      >
        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
      </MenuItem>,
      <MenuItem
        key="delete"
        onClick={() => handleOpenDeleteDialog(Number(row.id))}
        sx={{ m: 0, color: "error.main" }}
        disabled={isDeleting}
      >
        <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
      </MenuItem>,
    ],
  });

  return (
    <>
      <Back />
      <Title>System Registry</Title>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Frameworks, emulators and test sources registered for your account.
      </Typography>
      <MaterialReactTable table={table} />
      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Registry Value"
      >
        <JsonEditor
          minWidth={"100%"}
          data={selectedMetadata}
          restrictAdd={true}
          restrictDelete={true}
          restrictEdit={true}
        />
      </GlobalModal>

      <DialogConfirm
        open={deleteDialogOpen}
        title="Delete Registry Entry"
        content="Are you sure you want to delete this registry entry? This action cannot be undone."
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </>
  );
};

export default SystemRegistry;
